import React, { useState, useEffect } from "react";
import { Typography, Box, Stack, Paper, IconButton } from "@mui/material";
import TipPopup from "../TipPopup";
import WavePlayerFeed from "../Player/WavePlayerFeed";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseOutlinedIcon from "@mui/icons-material/PauseOutlined";
import { useTranslation } from "react-i18next";
import FavoriteButton from "../../common/FavoriteButton";
import { toast } from 'react-toastify';
import { Images } from "../../assets/common";
import { putStreamRecord, getUserId } from "../../constants/function";
import ApiService from "./../../services/api-service";
import { useHistory } from "react-router-dom";
import notifier from "../../components/Notifier";
import "./FeedCard.scss";

const FeedCard = ({
  item,
  index = 0,
  getName = () => "",
  handleClose = () => {},
  isPlaying = false,
  onPlay = () => {},
  cardRef,
  setSelectedAlbum,
  setShowPlaylist,
   userSaves = [],
  onSaveToggle = () => {}, 
  ...rest
}) => {
  const [open, setOpen] = useState(false);
  const [streamCounted, setStreamCounted] = useState(false);
  const [streamProgress, setStreamProgress] = useState(0); // Track percentage-based progress
  const { t } = useTranslation();
  const history = useHistory();

  // Reset stream progress when song changes or component unmounts
  useEffect(() => {
    setStreamProgress(0);
    setStreamCounted(false);
  }, [item._id]); // Reset when song ID changes

  // Helper function to extract artist ID
  const getArtistId = (artist) => {
    if (!artist) return null;
    
    // If artist is a string, it might be the ID itself
    if (typeof artist === 'string' && artist !== 'undefined' && artist !== 'null') {
      return artist;
    }
    
    // If artist is an object, check common ID fields
    if (typeof artist === 'object') {
      // Check common ID fields in order of preference
      // IMPORTANT: uId is the actual user ID for artists in artistData
      const possibleIds = [
        artist.uId,  // This is the actual user ID in artistData
        artist.uid,  // Alternative casing
        artist.userId,
        artist._id,
        artist.id, 
        artist.artistId,
        artist.artist?._id,
        artist.artist?.id,
        artist.uploader?._id,
        artist.uploader?.id
      ];
      
      // Find first valid ID
      for (const id of possibleIds) {
        if (id && id !== 'undefined' && id !== 'null') {
          return id;
        }
      }
      
      // If artist object has a nested artist property with string ID
      if (typeof artist.artist === 'string' && artist.artist !== 'undefined' && artist.artist !== 'null') {
        return artist.artist;
      }
    }
    
    return null;
  };

  useEffect(() => {
    setStreamCounted(false); // Reset stream count when song changes or play state changes
    console.log("FeedCard: Reset streamCounted for song", item?._id, "isPlaying:", isPlaying);
  }, [item._id, isPlaying]);

  if (!item) return null;

  console.log("FeedCard item:", item);
  console.log("FeedCard artistData:", item.artistData);
  console.log("FeedCard uploader:", item.uploader);
  if (item.artistData && item.artistData[0]) {
    console.log("FeedCard artistData[0] structure:", JSON.stringify(item.artistData[0], null, 2));
  }


  // Send stream event to Kinesis
  const sendKinesisStream = (currentTime) => {
    const payload = {
      eventType: "stream",
      timestamp: Date.now(),
      userId: getUserId(),
      musicId: item._id,
      playlistId: null,
      livestreamId: null,
      songDetails: item,
      timeInSong: currentTime,
      associationId: `${item._id}_${Date.now()}`,
      sessionId: localStorage.getItem("sessionId") || "sessionId",
      albumId: item.albumId,
    };
    console.log("Kinesis stream event payload:", payload);
    putStreamRecord(payload);
  };

  // Submit stream count to API
  const submitStreamCount = async (amount) => {
    try {
      const payload = {
        userId: getUserId(),
        musicId: item._id,
        inPlaylist: false,
        streamCount: amount,
      };
      
      console.log(`🎵 FEED STREAM COUNT: Submitting ${amount} for song: ${item.title}`);
      const response = await ApiService.STREAM_ADD(payload);
      console.log("🎵 FEED STREAM COUNT: API Response:", response);
      
      // Update local progress tracking
      const newProgress = streamProgress + amount;
      setStreamProgress(newProgress);
      
    } catch (error) {
      console.error("🎵 FEED STREAM COUNT: Error submitting stream count:", error);
    }
  };

  // Track stream progress at 25% intervals (percentage-based counting)
  const handleCurrentTime = (currentTime, duration) => {
    if (!duration || duration <= 0) return;
    
    // Calculate percentage of song played
    const percentage = (currentTime / duration) * 100;
    
    // Debug logging for progress tracking
    if (percentage > 0 && Math.floor(percentage) % 10 === 0 && Math.floor(percentage) !== Math.floor(streamProgress * 100)) {
      console.log(`🎵 FEED PROGRESS DEBUG: ${percentage}% reached, streamProgress: ${streamProgress}, song: ${item.title}`);
    }
    
    // Submit stream count at 25% intervals
    if (percentage >= 25 && streamProgress < 0.25) {
      console.log(`🎵 FEED STREAM COUNT: 25% reached for song: ${item.title}`);
      sendKinesisStream(currentTime); // Keep kinesis stream for analytics
      submitStreamCount(0.25);
    } else if (percentage >= 50 && streamProgress < 0.50) {
      console.log(`🎵 FEED STREAM COUNT: 50% reached for song: ${item.title}`);
      submitStreamCount(0.25);
    } else if (percentage >= 75 && streamProgress < 0.75) {
      console.log(`🎵 FEED STREAM COUNT: 75% reached for song: ${item.title}`);
      submitStreamCount(0.25);
    } else if (percentage >= 95 && streamProgress < 0.95) {
      console.log(`🎵 FEED STREAM COUNT: 95% reached for song: ${item.title}`);
      submitStreamCount(0.25);
    }
  };

  return (
    <Box className="feed-card-bg">
      <Paper
        elevation={6}
        className="feed-item"
        ref={cardRef}
        data-idx={rest["data-idx"]}
        onClick={onPlay}
        style={{
          border: isPlaying ? "1px solid #ffee20" : undefined,
          "--bg-img": `url(${item.thumbnail})`,
          background: "rgba(0,0,0,0.70)",
          backgroundBlendMode: "overlay"
        }}
      >
        {/* Title & Artist */}
        <Box className="feed-header">
          <Typography className="feed-title" variant="h6">
            {item.title}
          </Typography>
          <Typography className="feed-artist" variant="subtitle2">
            {/* Use artistData if available, otherwise fall back to uploader */}
            {item.artistData && item.artistData[0] ? (
              <>
                <span
                  style={{ cursor: "pointer", textDecoration: "none" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const artistId = getArtistId(item.artistData[0]);
                    console.log("Artist click - extracted ID:", artistId, "from data:", item.artistData[0]);
                    console.log("Using uId:", item.artistData[0].uId, "vs _id:", item.artistData[0]._id);
                    if (artistId) {
                      history.push(`/app/ArtistProfile/${artistId}`);
                    } else {
                      console.error("Artist ID not found for:", item.artistData[0]);
                      notifier.error("Unable to view artist profile");
                    }
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.textDecoration = "underline";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.textDecoration = "none";
                  }}
                >
                  {item.artistData[0].displayArtistName}
                </span>
                {item.artistData[1] && (
                  <>
                    {" "}
                    <span style={{ color: "#bbb" }}>&nbsp;feat.&nbsp;</span>
                    <span
                      style={{ cursor: "pointer", textDecoration: "none" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const artistId = getArtistId(item.artistData[1]);
                        console.log("Featured artist click - extracted ID:", artistId, "from data:", item.artistData[1]);
                        if (artistId) {
                          history.push(`/app/ArtistProfile/${artistId}`);
                        } else {
                          console.error("Featured artist ID not found for:", item.artistData[1]);
                          notifier.error("Unable to view artist profile");
                        }
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.textDecoration = "underline";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.textDecoration = "none";
                      }}
                    >
                      {item.artistData[1].displayArtistName}
                    </span>
                  </>
                )}
              </>
            ) : item.uploader ? (
              // Fallback to uploader if artistData is not available
              <span
                style={{ cursor: "pointer", textDecoration: "none" }}
                onClick={(e) => {
                  e.stopPropagation();
                  const uploaderId = getArtistId(item.uploader);
                  if (uploaderId) {
                    history.push(`/app/ArtistProfile/${uploaderId}`);
                  } else {
                    console.error("Uploader ID not found for:", item.uploader);
                  }
                }}
                onMouseEnter={(e) => {
                  e.target.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.target.style.textDecoration = "none";
                }}
              >
                {typeof item.uploader === 'object' ? 
                  (item.uploader.displayArtistName || item.uploader.firstName || 'Unknown Artist') : 
                  'Unknown Artist'}
              </span>
            ) : (
              <span>Unknown Artist</span>
            )}
          </Typography>
        </Box>

        {/* Thumbnail */}
        <Box className="feed-thumbnail">
          <img src={item.thumbnail} alt={item.title} />
        </Box>

        {/* Action Buttons: Tip, Play, Save/Livestream */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          justifyContent="center"
          className="feed-actions"
        >
          {/* Tip Button */}
          <IconButton
            onClick={e => {
              e.stopPropagation();
              setOpen(!open);
              console.log("Tip button clicked for song", item._id);
            }}
            className="tip-btn _cp"
            title="Tip song"
          >
            <img src={Images.tip_player} alt="tip_player" style={{ width: 28, height: 28 }} />
          </IconButton>
          {/* Play Button */}
          <IconButton
            onClick={e => {
              e.stopPropagation();
              onPlay();
              console.log("Play button clicked for song", item._id, "isPlaying:", isPlaying);
              // Do not call stream here, handled in handleCurrentTime
            }}
            className="play-btn _cp"
            title={isPlaying ? "Pause" : "Play"}
            style={{ transform: "translateY(-10px)" }}
          >
            {!isPlaying ? (
              <PlayArrowRoundedIcon sx={{ fontSize: "30px" }} color="default" className="_cp" />
            ) : (
              <PauseOutlinedIcon sx={{ fontSize: "30px" }} color="default" className="_cp" />
            )}
          </IconButton>

         {/* Favorite Button - Now using the reusable component */}
<Box sx={{ display: 'flex', alignItems: 'center', transform: 'translateY(-10px)' }}>
          <FavoriteButton
            itemId={item._id}
            itemType="music"
            userSaves={userSaves}
            onSaveToggle={onSaveToggle}
            size={24}
                className="favorite-btn"

          />
            </Box>

          {/* Save/Livestream Button */}
          <IconButton
            title={t("dropMenu.m3")}
            onClick={() => {
              setSelectedAlbum(item._id);
              setShowPlaylist(true);
              console.log("Add to playlist/studio clicked for song", item._id);
            }}
          >
            <img
              src={Images.playlist_add}
              alt="add to studio"
              className="w24 h20"
            />
          </IconButton>
        </Stack>

        {/* Waveform Player */}
        <WavePlayerFeed
          className="feed-wave"
          url={item.filePath}
          sampleData={item.sampleData}
          volume={50}
          playing={isPlaying}
          handlePlay={() => {
            setStreamCounted(false);
            console.log("WavePlayerFeed: handlePlay for song", item._id);
          }}
          handlePause={() => {
            console.log("WavePlayerFeed: handlePause for song", item._id);
          }}
          handleCurrentTime={handleCurrentTime}
          handleEnd={() => {
            console.log("WavePlayerFeed: handleEnd for song", item._id);
          }}
          handleSeek={(seekTime) => {
            console.log("WavePlayerFeed: handleSeek for song", item._id, "seekTime:", seekTime);
          }}
        />

        {/* Song Details */}
        <Stack className="feed-details" direction="row" spacing={3}>
          <Box className="detail-box">
            <Typography variant="caption">Genre</Typography>
            <Typography variant="body2">{item.genre || "Unknown"}</Typography>
          </Box>
          <Box className="detail-box">
            <Typography variant="caption">Release Date</Typography>
            <Typography variant="body2">
              {item.releaseDate
                ? new Date(item.releaseDate).toLocaleDateString()
                : "N/A"}
            </Typography>
          </Box>
          <Box className="detail-box">
            <Typography variant="caption">Plays</Typography>
            <Typography variant="body2">{item.listens ?? 0}</Typography>
          </Box>
          {item.isExplicit && (
            <Box className="detail-box explicit">
              <Typography variant="caption">Explicit</Typography>
            </Box>
          )}
        </Stack>

        {/* Tip Popup */}
        {open && (
          <TipPopup
            artist={getName(item)}
            id={item._id}
            open={open}
            isArtist={false}
            handleClose={() => setOpen(false)}
          />
        )}
      </Paper>
    </Box>
  );
};

export default FeedCard;